import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import UIVisualizador from './UIVisualizador';
import './UIDocumentoUpload.css';

interface Documento {
    id: string;
    source: string | null;
    file: File | null;
    tipo: string;
    ordem: number;
    uploading: boolean;
    size?: number;
    originalName?: string;
    renamedName?: string;
}

interface UIDocumentoUploadProps {
    name: string;
    contId?: number | string;
    tipoDocumentoList: Array<{ id: number; title: string }>;
}

// Função para formatar tamanho do arquivo
const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Componente de uma linha de documento
function DocumentoRow({ documento, onRemove, onChangeTipo, onClick, tipoDocumentoList }: {
    documento: Documento;
    onRemove: () => void;
    onChangeTipo: (tipo: string) => void;
    onClick: () => void;
    tipoDocumentoList: Array<{ id: number; title: string }>;
}) {
    // Prioridade: renamedName > file.name > extrair do source > fallback
    const fileName = documento.renamedName ||
        documento.file?.name ||
        (typeof documento.source === 'string' ? documento.source.split('/').pop() : null) ||
        'Documento';
    const fileSize = documento.file?.size ? formatFileSize(documento.file.size) :
                     documento.size ? formatFileSize(documento.size) : '-';

    return (
        <tr className={documento.uploading ? 'uploading' : ''}>
            <td className="documento-name-cell" onClick={onClick} style={{ cursor: 'pointer' }}>
                {fileName}
            </td>
            <td className="documento-size-cell">{fileSize}</td>
            <td className="documento-tipo-cell" onClick={(e) => e.stopPropagation()}>
                <select
                    value={documento.tipo}
                    onChange={(e) => onChangeTipo(e.target.value)}
                    disabled={documento.uploading}
                >
                    <option value="">Selecione</option>
                    {tipoDocumentoList && tipoDocumentoList.map(tipo => (
                        <option key={tipo.id} value={tipo.id}>{tipo.title}</option>
                    ))}
                </select>
            </td>
            <td className="documento-actions-cell">
                <button
                    type="button"
                    className="documento-remove-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    disabled={documento.uploading}
                    title="Remover documento"
                >
                    <svg viewBox="0 0 16 16" width="14" height="14">
                        <path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.75.75 0 1 1 1.06 1.06L9.06 8l3.22 3.22a.75.75 0 1 1-1.06 1.06L8 9.06l-3.22 3.22a.75.75 0 0 1-1.06-1.06L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06z" />
                    </svg>
                </button>
            </td>
        </tr>
    );
}

export default function UIDocumentoUpload({ name, contId, tipoDocumentoList = [] }: UIDocumentoUploadProps) {
    const formContext = useFormContext();
    const { register, watch, setValue } = formContext;

    const [documentos, setDocumentos] = useState<Documento[]>([]);
    const [showVisualizador, setShowVisualizador] = useState(false);
    const [documentoUrl, setDocumentoUrl] = useState<string>('');

    // Inicializar documentos do form
    useEffect(() => {
        register(name);
        const formValue = watch(name);
        if (formValue && Array.isArray(formValue)) {
            const documentosIniciais: Documento[] = formValue
                .filter((d: any) => d && (d.documento || d.source))
                .map((d: any, index: number) => ({
                    id: d.id || `doc-${Date.now()}-${index}`,
                    source: (typeof d.documento === 'string' ? d.documento : null) ||
                            (typeof d.source === 'string' ? d.source : null),
                    file: null,
                    tipo: String(d.tipoDocumento || d.tipo || ''),
                    ordem: index,
                    uploading: false,
                    size: d.size || undefined,
                    originalName: d.originalName || undefined,
                    renamedName: d.renamedName || undefined,
                }));
            setDocumentos(documentosIniciais);
        }
    }, []);

    // Sincronizar documentos com form
    useEffect(() => {
        const formData = documentos.map((doc, index) => ({
            tipoDocumento: doc.tipo,
            tipo: doc.tipo,
            documento: doc.source,
            source: doc.source,
            size: doc.size,
            originalName: doc.originalName,
            renamedName: doc.renamedName,
            ordem: index,
        })).filter(d => d.documento);

        setValue(name, formData, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: false
        });
    }, [documentos, name, setValue]);

    // Upload de arquivo para o servidor
    const uploadFile = async (file: File, docId: string) => {
        try {
            const formData = new FormData();
            formData.append('files', file);
            if (contId) {
                formData.append('cont_id', String(contId));
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_ENDPOINT}/?route=contrato/form&action=documentoUpload`,
                {
                    method: 'POST',
                    body: formData,
                    credentials: 'include',
                }
            );

            const result = await response.json();

            if (result.status === 'success' && result.payload) {
                const payload = result.payload;
                setDocumentos(prev => prev.map(d =>
                    d.id === docId
                        ? {
                            ...d,
                            source: typeof payload === 'string' ? payload : payload.source,
                            size: typeof payload === 'object' ? payload.size : undefined,
                            originalName: typeof payload === 'object' ? payload.originalName : undefined,
                            renamedName: typeof payload === 'object' ? payload.renamedName : undefined,
                            uploading: false
                        }
                        : d
                ));
            } else {
                throw new Error(result.message || 'Erro ao enviar arquivo');
            }
        } catch (error) {
            console.error('Erro no upload:', error);
            alert('Erro ao enviar arquivo: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
            setDocumentos(prev => prev.filter(d => d.id !== docId));
        }
    };

    // Callback do dropzone
    const onDrop = (acceptedFiles: File[]) => {
        const novosDocumentos: Documento[] = acceptedFiles.map((file, index) => {
            const docId = `doc-${Date.now()}-${index}`;

            const documento: Documento = {
                id: docId,
                source: null,
                file,
                tipo: '',
                ordem: documentos.length + index,
                uploading: true,
            };

            uploadFile(file, docId);

            return documento;
        });

        setDocumentos(prev => [...prev, ...novosDocumentos]);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'image/*': ['.jpg', '.jpeg', '.png', '.gif']
        },
        multiple: true,
    });

    // Remover documento
    const handleRemove = (docId: string) => {
        setDocumentos(prev => prev.filter(d => d.id !== docId));
    };

    // Mudar tipo e renomear arquivo via AJAX imediatamente
    const handleChangeTipo = async (docId: string, tipo: string) => {
        const documento = documentos.find(d => d.id === docId);
        if (!documento) return;

        // Se não tem source (ainda não foi enviado) ou não tem contId, apenas atualiza o tipo localmente
        if (!documento.source || !contId) {
            setDocumentos(prev => prev.map(d =>
                d.id === docId ? { ...d, tipo } : d
            ));
            return;
        }

        // Marca como uploading para indicar que está processando
        setDocumentos(prev => prev.map(d =>
            d.id === docId ? { ...d, uploading: true, tipo } : d
        ));

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_ENDPOINT}/?route=contrato/form&action=renomearDocumento`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: new URLSearchParams({
                        cont_id: String(contId),
                        old_source: documento.source,
                        tipo: tipo,
                        original_name: documento.originalName || ''
                    }),
                    credentials: 'include',
                }
            );

            const result = await response.json();

            if (result.status === 'success' && result.payload) {
                // Atualiza com os novos dados retornados pelo servidor
                setDocumentos(prev => prev.map(d =>
                    d.id === docId
                        ? {
                            ...d,
                            source: result.payload.source,
                            renamedName: result.payload.renamedName,
                            originalName: result.payload.originalName || d.originalName,
                            tipo,
                            uploading: false
                        }
                        : d
                ));
            } else {
                throw new Error(result.message || 'Erro ao renomear documento');
            }
        } catch (error) {
            console.error('Erro ao renomear documento:', error);
            alert('Erro ao renomear documento: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
            // Reverte o tipo em caso de erro
            setDocumentos(prev => prev.map(d =>
                d.id === docId ? { ...d, uploading: false } : d
            ));
        }
    };

    // Abrir visualizador
    const handleDocumentoClick = (source: string | null) => {
        if (source) {
            setDocumentoUrl(source);
            setShowVisualizador(true);
        }
    };

    return (
        <>
            <div className="ui-documento-upload">
                {documentos.length > 0 && (
                    <table className="documentos-table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Tamanho</th>
                                <th>Tipo</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {documentos.map((documento) => (
                                <DocumentoRow
                                    key={documento.id}
                                    documento={documento}
                                    onRemove={() => handleRemove(documento.id)}
                                    onChangeTipo={(tipo) => handleChangeTipo(documento.id, tipo)}
                                    onClick={() => handleDocumentoClick(documento.source)}
                                    tipoDocumentoList={tipoDocumentoList}
                                />
                            ))}
                        </tbody>
                    </table>
                )}

                <div
                    {...getRootProps()}
                    className={`documento-add-btn ${isDragActive ? 'active' : ''}`}
                >
                    <input {...getInputProps()} />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>Adicionar Documento</span>
                </div>
            </div>

            <UIVisualizador
                show={showVisualizador}
                onClose={() => setShowVisualizador(false)}
                tipo="documento"
                documentoUrl={documentoUrl}
            />
        </>
    );
}
